import type { InvoiceAddress } from '../../flow/user/userModels';
import type { HybrisOrderEntry } from '../../models/hybris/hybrisCart';
import type { HybrisDeliveryOptions } from '../../models/hybris/hybrisDeliveryOptions';
import type {
    PaymentCode,
    SubscriptionFrequency,
} from '../../store/structureDefinitions/checkoutState';
import type { UserAddress } from '../../store/structureDefinitions/userState';
import type { ApiAddMultipleRecipesRequest } from '../recipe/requests/recipeApiRequests';
import type { ApiAddMultipleRecipesResponse } from '../recipe/responses/recipeApiReponses';
import type {
    ApiGetCartDriverMessageRequest,
    ApiModifyCartItemRequest,
    ApiPutCartDriverMessageRequest,
} from './requests/cartRequests';
import type {
    ApiCreatePunchoutReponse,
    ApiHmpPayment,
    ApiIntrumPayment,
    ApiKobePayment,
    ApiSubstituteProductResponse,
    ApiValidationCartResponse,
} from './responses/cartResponses';

import getDirectHybrisAxiosClient, {
    getUserIdUrlSegment,
} from '../clients/directHybrisAxiosClient';
import hybrisAxiosClient from '../clients/hybrisAxiosClient';
import { validateCartErrorInterceptor } from './cartApiErrorInterceptors';

const userIdSegment = (userId: string) => getUserIdUrlSegment(userId);

const buyMultipleRecipes = async (request: ApiAddMultipleRecipesRequest) => {
    const response = await hybrisAxiosClient.post<ApiAddMultipleRecipesResponse>(
        `carts/current/recipes`,
        request,
    );
    return response.data;
};

const changeCartItemVariant = (request: ApiModifyCartItemRequest): Promise<void> =>
    hybrisAxiosClient.put(`carts/current/items`, request);

const changeRecipePortions = (recipeId: string, portions: number) =>
    hybrisAxiosClient.put(`carts/current/recipe/${recipeId}/portions/${portions}`);

const removeRecipe = async (recipeId: string): Promise<void> =>
    hybrisAxiosClient.delete(`carts/current/recipe/${recipeId}`);

const getProductSubstitutes = async (rowId: string) => {
    const substituteProducts = await hybrisAxiosClient.get<{ id: string }[]>(
        `/carts/current/recipe/${rowId}`,
    );
    return substituteProducts.data;
};

const substituteProduct = async (
    rowId: string,
    productId: number | string,
): Promise<ApiSubstituteProductResponse> => {
    const response = await hybrisAxiosClient.post<ApiSubstituteProductResponse>(
        `/carts/current/recipe/${encodeURI(rowId)}/${productId}`,
    );

    return response.data;
};

const getDeliveryAddressList = async (): Promise<UserAddress[]> => {
    const response = await hybrisAxiosClient.get('/carts/current/addresses/delivery/list');
    return response.data;
};

const getDeliveryAddress = async (): Promise<UserAddress> => {
    const response = await hybrisAxiosClient.get('/carts/current/address/delivery');
    return response.data;
};

const setDeliveryAddress = async (id: string): Promise<UserAddress> => {
    const response = await hybrisAxiosClient.put(
        `/carts/current/address/delivery?addressId=${encodeURIComponent(id)}`,
    );
    return response.data;
};

const removeDeliveryAddress = async (id: string): Promise<void> => {
    await hybrisAxiosClient.put(
        `/carts/current/address/delivery?addressId=${encodeURIComponent(id)}`,
    );
};

const getPostalCodeDeliveryOptions = async (
    userId: string,
    cartId: string,
    postalCode: string,
    token?: string,
): Promise<HybrisDeliveryOptions> => {
    const client = getDirectHybrisAxiosClient(token);
    const response = await client.get<HybrisDeliveryOptions>(
        `/users/${userIdSegment(userId)}/carts/${cartId}/deliveryoptions`,
        { params: { postcode: postalCode } },
    );
    return response.data;
};

/** Ecommerce GET /users/{userId}/carts/{cartId}/drivermessage → `{ message }` */
const getDriverMessage =
    (token: string) =>
    async ({
        userId,
        cartId,
    }: ApiGetCartDriverMessageRequest): Promise<{ driverMessage: string }> => {
        const response = await getDirectHybrisAxiosClient(token).get<{ message?: string }>(
            `users/${encodeURIComponent(userId)}/carts/${encodeURIComponent(cartId)}/drivermessage`,
        );
        return { driverMessage: response.data.message ?? '' };
    };

/** Ecommerce PUT: form field `driverMessageWsDTO` = JSON `{ message }` */
const buildDriverMessagePutBody = (message: string): URLSearchParams =>
    new URLSearchParams({
        driverMessageWsDTO: message,
    });

const putDriverMessage =
    (token: string) =>
    async ({ userId, cartId, message }: ApiPutCartDriverMessageRequest): Promise<void> => {
        await getDirectHybrisAxiosClient(token).put(
            `users/${encodeURIComponent(userId)}/carts/${encodeURIComponent(cartId)}/drivermessage`,
            buildDriverMessagePutBody(message),
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            },
        );
    };

const setPaymentMethod = async (code: PaymentCode): Promise<void> => {
    await hybrisAxiosClient.put(
        `carts/current/paymentmethod?paymentMethod=${encodeURIComponent(code)}`,
    );
};

// matkonto and kobe use the same init endpoint!?
const initializeMatkontoPayment = async (): Promise<ApiKobePayment> => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/matkonto');
    return response.data;
};
// matkonto and kobe use the same init endpoint!?
const initializeKobePayment = async (): Promise<ApiKobePayment> => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/kobe');
    return response.data;
};
const initializeIntrumPayment = async (): Promise<ApiIntrumPayment> => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/intrum');
    return response.data;
};
const initializeHmpPayment = async (): Promise<ApiHmpPayment> => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/hmp');
    return response.data;
};
const initializeEdiPayment = async (): Promise<ApiIntrumPayment> => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/edi');
    return response.data;
};

/** Cart should be validated before sent to punchout, i think we also could use this one for "normal orders" */
const validateCart = async ({
    userId,
    token,
}: {
    userId: string;
    token: string;
}): Promise<ApiValidationCartResponse> => {
    const response = await getDirectHybrisAxiosClient(token, [
        validateCartErrorInterceptor,
    ]).post<ApiValidationCartResponse>(`users/${userIdSegment(userId)}/carts/current/validation`);
    return response.data;
};

// samlingsfaktura uses the same endpoint as Intrum
const initializeSamlingsFakturaPayment = async () => {
    const response = await hybrisAxiosClient.get('carts/current/paymentmethod/init/intrum');
    return response.data;
};

const initializeKlarnaPayment = async (
    newSession: boolean,
    subscriptionFrequency: SubscriptionFrequency | null,
): Promise<string> => {
    const response = await hybrisAxiosClient.post(
        `carts/current/klarna?newSession=${newSession}&subscription=${!!subscriptionFrequency}&frequency=${
            subscriptionFrequency ? subscriptionFrequency.toString() : ''
        }`,
    );
    return response.data;
};

const getBillingAddress = async (): Promise<InvoiceAddress> => {
    const response = await hybrisAxiosClient.get('carts/current/address/billing');
    return response.data;
};

const setBillingAddress = async (addressId: string): Promise<InvoiceAddress> => {
    const response = await hybrisAxiosClient.put(
        `carts/current/address/billing?addressId=${encodeURIComponent(addressId)}`,
    );
    return response.data;
};

const removeBillingAddress = async (): Promise<InvoiceAddress> => {
    const response = await hybrisAxiosClient.delete('carts/current/address/billing');
    return response.data;
};

const setItemsReplaceability = async (arg: { replaceAll: boolean }): Promise<void> => {
    await hybrisAxiosClient.put('/carts/current/replaceAll', arg);
};

/* cart?fields=FULL does not return this field so we cannot just refetch cart :O
but we can ask just for requisitionExpireTime from current cart, 
altho we should have this field either in fields=FULL always or a separate endpoint like with driver message
*/
const getRequisitionExpireTime = (token: string) => async (userId: string) => {
    const response = await getDirectHybrisAxiosClient(token).get<{ requisitionExpireTime: string }>(
        `users/${userIdSegment(userId)}/carts/current?fields=requisitionExpireTime`,
    );

    return response.data;
};

/* cart?fields=FULL does not return this field so we cannot just refetch cart :O
but we can ask just for internalReference from current cart, 
altho we should have this field either in fields=FULL always or a separate endpoint like with driver message
*/
const getInternalReference = (token: string) => async (userId: string) => {
    const response = await getDirectHybrisAxiosClient(token).get<{ internalReference: string }>(
        `users/${userIdSegment(userId)}/carts/current?fields=internalReference`,
    );

    return response.data;
};

const setInternalReference =
    (token: string) => async (userId: string, internalReference: string) => {
        await getDirectHybrisAxiosClient(token).put(
            `users/${userIdSegment(userId)}/carts/current/punchout/internalreference`,
            {
                internalreference: internalReference,
            },
        );
    };

const getContactPhoneNumber = (token: string) => async (userId: string) => {
    const response = await getDirectHybrisAxiosClient(token).get<{ phone: string } | {}>(
        `users/${userIdSegment(userId)}/carts/current/punchout/contact`,
    );

    return response.data;
};

const setContactPhoneNumber = (token: string) => async (userId: string, phone: string) => {
    await getDirectHybrisAxiosClient(token).put(
        `users/${userIdSegment(userId)}/carts/current/punchout/contact`,
        {
            phone,
        },
    );
};

// not sure yet if we need this
const createPunchout = (token: string) => async (userId: string) => {
    const response = await getDirectHybrisAxiosClient(token).post<ApiCreatePunchoutReponse>(
        `users/${userIdSegment(userId)}/carts/current/punchout/requisition`,
    );

    return response.data;
};

export const validateAssortmentApiUrl = (userId: string) =>
    `users/${userIdSegment(userId)}/carts/current/entries/not-in-assortment`;

const validateAssortment = (token: string | undefined) => async (userId: string) => {
    const response = await getDirectHybrisAxiosClient(token).get<{
        orderEntries: HybrisOrderEntry[];
    }>(validateAssortmentApiUrl(userId));

    return response.data;
};

const deliveryAddresses = {
    fetchAll: getDeliveryAddressList,
    select: setDeliveryAddress,
    remove: removeDeliveryAddress,
    current: getDeliveryAddress,
};

const billingAddress = {
    select: setBillingAddress,
    remove: removeBillingAddress,
    current: getBillingAddress,
};

const punchout = {
    getInternalReference,
    setInternalReference,
    create: createPunchout,
    getRequisitionExpireTime,
    validateCart,
    getContactPhoneNumber,
    setContactPhoneNumber,
};

const recipes = {
    buyMultipleRecipes,
    removeRecipe,
    substituteProduct,
    getProductSubstitutes,
    changeRecipePortions,
};

const driverMessage = {
    get: getDriverMessage,
    put: putDriverMessage,
};

const payment = {
    set: setPaymentMethod,
    initializeMatkontoPayment,
    initializeKobePayment,
    initializeIntrumPayment,
    initializeHmpPayment,
    initializeEdiPayment,
    initializeKlarnaPayment,
    initializeSamlingsFakturaPayment,
};

const cartApi = {
    deliveryAddresses,
    recipes,
    changeCartItemVariant,
    getPostalCodeDeliveryOptions,
    driverMessage,
    setItemsReplaceability,
    payment,
    billingAddress,
    punchout,
    validateAssortment,
};

export default cartApi;
